import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubstackCustomForm } from '@/components/newsletter/SubstackEmbed';

export function Footer() {
  const { t, i18n } = useTranslation();
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-aspexit-black text-white mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Colonne 1 : Branding + Newsletter */}
          <div className="lg:col-span-1">
            <Link to={`/${currentLocale}`} className="inline-block mb-4">
              <div className="text-2xl font-heading font-bold text-white">
                Aspexit
              </div>
              <div className="text-sm text-gray-400">
                {t('footer.tagline')}
              </div>
            </Link>

            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              {t('footer.description')}
            </p>

            {/* Newsletter inline */}
            <div className="mb-4">
              <h4 className="font-semibold mb-2 text-white">
                {t('footer.newsletterTitle')}
              </h4>
              <p className="text-sm text-gray-400 mb-3">
                {t('footer.newsletterDescription')}
              </p>
              <SubstackCustomForm
                placeholder={t('newsletter.emailPlaceholder')}
                buttonText={t('footer.newsletterSubscribe')}
              />
            </div>
          </div>

          {/* Colonne 2 : Explorer */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">
              {t('footer.explore')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to={`/${currentLocale}`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLocale}/blog`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLocale}/formations`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('nav.formations')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLocale}/prestations`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('nav.prestations')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLocale}/glossaire`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('footer.glossary', 'Glossaire AgriTech')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Colonne 3 : Contact & Réseaux */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to={`/${currentLocale}/contact`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('footer.contactUs')}
                </Link>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/corentin-leroux-0225a182/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>

          {/* Colonne 4 : Légal */}
          <div>
            <h3 className="text-lg font-heading font-semibold mb-4">
              {t('footer.legalSection')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to={`/${currentLocale}/mentions-legales`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('footer.legal')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLocale}/confidentialite`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${currentLocale}/cgv`}
                  className="text-gray-300 hover:text-aspexit-green transition-colors text-sm"
                >
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          <p>{t('footer.copyright', { year: currentYear })}</p>
        </div>
      </div>
    </footer>
  );
}
